import {
  DataGrid,
  GridColDef,
  Icon,
  IconButton,
  Input,
  InputBoolean,
  MultiSelect,
  Tooltip,
} from '@fashionnovateam/nova-ui-toolkit';
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { ImageFileInput } from '../../ReusableComponents/ImageFileInput/ImageFileInput';
import { AddedItem, ItemFieldError, ItemsErrors } from '../types/items.types';
import { useAddItems, useAddItemsDispatch } from './AddItemsContext';
import { useGetMetaDatas } from '../../../Lib/Hooks/MetadataService';
import { useGetVendorByUserId } from '../../../Lib/Hooks/vendor.hooks';
import { CountryListSelectInputOptions } from '../../../Lib/Constants/countries';

type AddItemsGridProps = {
  errors: ItemsErrors;
  setFieldError: (
    itemId: string,
    fieldName: string,
    error: ItemFieldError,
  ) => void;
};

/**
 * A component that renders a grid of items with various interactive inputs, including options
 * for adding, updating, and removing items. The grid supports fields such as photos, style
 * number, category, cost inputs, and lead time, along with validation and error handling.
 *
 * @param {Object} args - The argument object containing properties for the grid.
 * @param {Object} args.errors - An object containing validation errors keyed by item ID and field name.
 * @param {Function} args.setFieldError - A function to update the validation error state for a specific field of an item.
 */
function AddItemsGrid({ errors, setFieldError }: AddItemsGridProps) {
  const items = useAddItems();
  const itemsDispatch = useAddItemsDispatch();
  const { data: metaDatas } = useGetMetaDatas([
    'ns_master_merchHierarchy',
    'ns_master_vendorsMerchType',
  ]);
  const { id: userId } = useParams<{
    id: string;
  }>();
  const { data: vendorResponse } = useGetVendorByUserId(userId);

  const divisionOptions = useMemo(() => {
    if (!metaDatas?.ns_master_merchHierarchy?.divisions) {
      return [];
    }
    return Object.keys(metaDatas.ns_master_merchHierarchy.divisions).map(
      (division) => ({
        label: division,
        value: division,
      }),
    );
  }, [metaDatas]);

  const columns: GridColDef[] = [
    {
      renderHeader: () => (
        <div className="flex flex-row items-center justify-center gap-1">
          Buyer Pick
          <Tooltip
            arrow
            content="A ‘Buyer Pick’ means that you’ve already shown the product a Buyer and they selected it as something that want to buy."
            position="top"
            zIndex={100}
          >
            <Icon name="information/information-circle" />
          </Tooltip>
        </div>
      ),
      field: 'buyer_pick',
      headerName: 'Buyer Pick',
      render: 'custom',
      display: 'flex',
      flex: 0,
      sortable: false,
      minWidth: 120,
      width: 120,
      disableColumnMenu: true,
      resizable: false,
    },
    {
      field: 'front_photo',
      headerName: 'Front Photo',
      render: 'custom',
      display: 'flex',
      flex: 0,
      sortable: false,
      minWidth: 90,
      width: 90,
      disableColumnMenu: true,
      resizable: false,
    },
    {
      renderHeader: () => (
        <div className="flex flex-row items-center justify-center gap-1">
          Swatch Photo
          <Tooltip
            arrow
            content="Upload a photo of the swatches you have available for this style."
            position="top"
            zIndex={100}
          >
            <Icon name="information/information-circle" />
          </Tooltip>
        </div>
      ),
      field: 'swatch_photo',
      headerName: 'Swatch Photo',
      render: 'custom',
      display: 'flex',
      flex: 0,
      sortable: false,
      minWidth: 120,
      width: 120,
      disableColumnMenu: true,
      resizable: false,
    },
    {
      field: 'style_number',
      headerName: 'Style #',
      render: 'custom',
      display: 'flex',
      flex: 0,
      sortable: false,
      minWidth: 150,
      width: 150,
      disableColumnMenu: true,
      resizable: false,
    },
    {
      field: 'jr_cost',
      headerName: 'JR Cost',
      render: 'custom',
      display: 'flex',
      flex: 0,
      sortable: false,
      minWidth: 130,
      width: 130,
      disableColumnMenu: true,
      resizable: false,
    },
    {
      field: 'plus_cost',
      headerName: 'Plus Cost',
      render: 'custom',
      display: 'flex',
      flex: 0,
      sortable: false,
      minWidth: 130,
      width: 130,
      disableColumnMenu: true,
      resizable: false,
    },
    {
      field: 'lead_time_in_weeks',
      headerName: 'Lead Time in Weeks',
      render: 'custom',
      display: 'flex',
      flex: 0,
      sortable: false,
      minWidth: 150,
      width: 150,
      disableColumnMenu: true,
      resizable: false,
    },
    {
      renderHeader: () => (
        <div className="flex flex-row items-center justify-center gap-1">
          Fit Sample
          <Tooltip
            arrow
            content="Item has previously been fitted and can be used as a fit sample."
            position="top"
            zIndex={100}
          >
            <Icon name="information/information-circle" />
          </Tooltip>
        </div>
      ),
      field: 'fit_sample',
      headerName: 'Fit Sample',
      render: 'custom',
      display: 'flex',
      flex: 1,
      sortable: false,
      minWidth: 130,
      width: 130,
      disableColumnMenu: true,
      resizable: false,
    },
    {
      renderHeader: () => (
        <div className="flex flex-row items-center justify-center gap-1">
          FN Exclusive
          <Tooltip
            arrow
            content="Product is only being sold to Fashion Nova."
            position="top"
            zIndex={100}
          >
            <Icon name="information/information-circle" />
          </Tooltip>
        </div>
      ),
      field: 'fashionnova_exclusive',
      headerName: 'FN Exclusive',
      render: 'custom',
      display: 'flex',
      flex: 1,
      sortable: false,
      minWidth: 130,
      width: 130,
      disableColumnMenu: true,
      resizable: false,
    },
    {
      field: 'country_of_origin',
      headerName: 'COO',
      render: 'custom',
      display: 'flex',
      flex: 1,
      sortable: false,
      minWidth: 150,
      width: 300,
      disableColumnMenu: true,
      resizable: false,
    },
    {
      field: 'division',
      headerName: 'Division',
      render: 'custom',
      display: 'flex',
      flex: 1,
      sortable: false,
      minWidth: 150,
      width: 300,
      disableColumnMenu: true,
      resizable: false,
    },
    {
      field: 'department',
      headerName: 'Department',
      render: 'custom',
      display: 'flex',
      flex: 1,
      sortable: false,
      minWidth: 150,
      width: 300,
      disableColumnMenu: true,
      resizable: false,
    },
    {
      field: 'category',
      headerName: 'Category',
      render: 'custom',
      display: 'flex',
      flex: 1,
      sortable: false,
      minWidth: 150,
      width: 300,
      disableColumnMenu: true,
      resizable: false,
    },
    {
      field: 'class',
      headerName: 'Class',
      render: 'custom',
      display: 'flex',
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
      resizable: false,
      minWidth: 150,
      width: 300,
    },
    {
      field: 'trash',
      headerName: '',
      render: 'custom',
      display: 'flex',
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
      resizable: false,
    },
  ];

  const removeItem = (id: AddedItem['id']) => {
    if (items.length <= 1) {
      return;
    }
    itemsDispatch({ type: 'REMOVED', payload: { id } });
  };

  /**
   * Updates a specific field of an item within a list of items, identified by its unique id.
   *
   * @template K - A key of the `AddedItem` type, representing the field to update.
   * @param {AddedItem['id']} id - The unique identifier of the item to be updated.
   * @param {K} field - The field of the item that needs to be updated.
   * @param {AddedItem[K]} value - The new value to assign to the specified field.
   *
   * @description This function performs an update on a list of items by locating the specified item
   * using its unique `id` and then updating the provided field with the given value. After the update,
   * the function also clears any validation errors associated with the updated field for the specific item.
   */
  const updateItem = <K extends keyof AddedItem>(
    id: AddedItem['id'],
    field: K,
    value: AddedItem[K] | null,
  ) => {
    itemsDispatch({
      type: 'CHANGED',
      payload: { id, updates: { [field]: value } },
    });
    setFieldError(id, field, { isInvalid: false });
  };

  /**
   * A function that prevents the propagation of specific keyboard events on the DataGrid.
   *
   * This function stops the propagation of the keydown event when the pressed key is
   * one of the following: 'ArrowLeft', 'ArrowRight', or the space bar (' ').
   *
   * @param {React.KeyboardEvent} event - The keyboard event object triggered by a key press.
   */
  const stopKeyDownPropagation = (event: React.KeyboardEvent) => {
    if (
      event.key === 'ArrowLeft' ||
      event.key === 'ArrowRight' ||
      event.key === ' '
    ) {
      event.stopPropagation();
    }
  };

  const vendorMerchType = useMemo(() => {
    if (metaDatas?.ns_master_vendorsMerchType && vendorResponse) {
      const vendorFound = metaDatas?.ns_master_vendorsMerchType.find(
        (vMerchType) =>
          vMerchType.vendorId === vendorResponse.vendor.internal_id,
      );
      return vendorFound ?? null;
    }
    return null;
  }, [metaDatas?.ns_master_vendorsMerchType, vendorResponse]);

  const countryOfOriginOptions = useMemo(() => {
    if (!vendorMerchType?.domesticMerchPoEligible) {
      return CountryListSelectInputOptions.filter(
        (country) => country.value !== 'US',
      );
    }
    return CountryListSelectInputOptions;
  }, [vendorMerchType]);

  const isBuyerPickDisabled = (
    division?: string | null,
    category?: string | null,
  ) => {
    const isWomenDivision = division?.toLowerCase().includes('women');
    const isShoeCategory = category?.toLowerCase().includes('shoe');
    return isWomenDivision && isShoeCategory;
  };

  const transformedRows = useMemo(
    () =>
      items.map((item) => ({
        id: item.id,
        buyer_pick: (() => {
          const disabled = isBuyerPickDisabled(item.division, item.category);
          return (
            <div
              className={`flex items-center gap-1 ${disabled ? 'opacity-50' : ''}`}
            >
              <InputBoolean
                key={`buyer-pick-${item.id}-${disabled}`}
                data-testid="buyer-pick-input"
                error={errors[`${item.id}-buyer_pick`]?.isInvalid}
                checked={item.buyer_pick}
                disabled={disabled}
                onChange={(v) => {
                  updateItem(item.id, 'buyer_pick', v.target.checked);
                }}
              />
              {disabled && (
                <Tooltip
                  arrow
                  content="Buyer Pick is available for select categories only."
                  position="top"
                  zIndex={100}
                >
                  <Icon
                    data-testid="buyer-pick-disabled-info"
                    name="information/information-circle"
                    className="text-base-disabled"
                  />
                </Tooltip>
              )}
            </div>
          );
        })(),
        front_photo: (
          <ImageFileInput
            data-testid="front-photo-input"
            error={errors[`${item.id}-front_photo`]?.isInvalid}
            id={`front_photo_${item.id}`}
            label="Add Front Photo"
            onFileSelect={(file) => {
              updateItem(item.id, 'front_photo', file);
            }}
            name="front"
          />
        ),
        swatch_photo: (
          <ImageFileInput
            data-testid="swatch-photo-input"
            error={errors[`${item.id}-swatch_photo`]?.isInvalid}
            id={`swatch_photo_${item.id}`}
            label="Add Swatch Photo"
            onFileSelect={(file) => {
              updateItem(item.id, 'swatch_photo', file);
            }}
            name="swatch"
          />
        ),
        style_number: (
          <Input
            data-testid="style-number-input"
            error={errors[`${item.id}-style_number`]?.isInvalid}
            placeholder="Add"
            defaultValue=""
            value={item.style_number || ''}
            onKeyDown={stopKeyDownPropagation}
            onChange={(v) => {
              updateItem(item.id, 'style_number', v.target.value);
            }}
          />
        ),
        fit_sample: (
          <InputBoolean
            data-testid="fit-sample-input"
            error={errors[`${item.id}-fit_sample`]?.isInvalid}
            checked={item.fit_sample}
            onChange={(v) => {
              updateItem(item.id, 'fit_sample', v.target.checked);
            }}
          />
        ),
        fashionnova_exclusive: (
          <InputBoolean
            data-testid="fashionnova-exclusive-input"
            error={errors[`${item.id}-fashionnova_exclusive`]?.isInvalid}
            checked={item.fashionnova_exclusive}
            onChange={(v) => {
              updateItem(item.id, 'fashionnova_exclusive', v.target.checked);
            }}
          />
        ),
        country_of_origin: (
          <MultiSelect
            data-testid="country-of-origin-select"
            className="w-full"
            error={errors[`${item.id}-country_of_origin`]?.isInvalid}
            placeholder="Select..."
            value={
              item.country_of_origin ? [item.country_of_origin] : undefined
            }
            options={countryOfOriginOptions}
            searchable
            singleSelectWhenSearchable
            portal
            onChange={(v) => {
              updateItem(item.id, 'country_of_origin', v[0] || null);
            }}
          />
        ),
        division: (
          <MultiSelect
            data-testid="division-select"
            className="w-full"
            error={errors[`${item.id}-division`]?.isInvalid}
            placeholder="Select..."
            value={item.division ? [item.division] : undefined}
            options={divisionOptions}
            searchable
            singleSelectWhenSearchable
            portal
            onChange={(v) => {
              updateItem(item.id, 'division', v[0] || null);
              updateItem(item.id, 'department', null);
              updateItem(item.id, 'category', null);
              updateItem(item.id, 'class', null);
            }}
          />
        ),
        department: (
          <MultiSelect
            data-testid="department-select"
            className="w-full"
            error={errors[`${item.id}-department`]?.isInvalid}
            placeholder="Select..."
            value={item.department ? [item.department] : undefined}
            disabled={!item.division}
            options={
              item.division
                ? Object.keys(
                    metaDatas?.ns_master_merchHierarchy?.divisions?.[
                      item.division
                    ] ?? {},
                  ).map((d) => ({ label: d, value: d }))
                : []
            }
            searchable
            singleSelectWhenSearchable
            portal
            onChange={(v) => {
              updateItem(item.id, 'department', v[0] || null);
              updateItem(item.id, 'category', null);
              updateItem(item.id, 'class', null);
            }}
          />
        ),
        category: (
          <MultiSelect
            data-testid="category-select"
            className="w-full"
            error={errors[`${item.id}-category`]?.isInvalid}
            placeholder="Select..."
            value={item.category ? [item.category] : undefined}
            disabled={!item.division || !item.department}
            options={
              item.division && item.department
                ? Object.keys(
                    metaDatas?.ns_master_merchHierarchy?.divisions?.[
                      item.division
                    ]?.[item.department] || {},
                  ).map((c) => ({ label: c, value: c }))
                : []
            }
            searchable
            singleSelectWhenSearchable
            portal
            onChange={(v) => {
              const newCategory = v[0] || null;
              updateItem(item.id, 'category', newCategory);
              updateItem(item.id, 'class', null);

              if (isBuyerPickDisabled(item.division, newCategory)) {
                updateItem(item.id, 'buyer_pick', false);
              }
            }}
          />
        ),
        class: (
          <MultiSelect
            data-testid="class-select"
            className="w-full"
            error={errors[`${item.id}-class`]?.isInvalid}
            placeholder="Select..."
            value={item.class ? [item.class] : undefined}
            disabled={!item.category || !item.department || !item.division}
            options={
              item.division && item.department && item.category
                ? Object.keys(
                    metaDatas?.ns_master_merchHierarchy?.divisions?.[
                      item.division
                    ]?.[item.department]?.[item.category] || {},
                  ).map((c) => ({ label: c, value: c }))
                : []
            }
            searchable
            singleSelectWhenSearchable
            portal
            onChange={(v) => {
              updateItem(item.id, 'class', v[0] || null);
            }}
          />
        ),
        jr_cost: (
          <Input
            data-testid="jr-cost-input"
            error={errors[`${item.id}-jr_cost`]?.isInvalid}
            startAdornment="$"
            placeholder="0"
            type="number"
            value={item.jr_cost ?? ''}
            onKeyDown={stopKeyDownPropagation}
            onChange={(v) => {
              if (v.target.value === '') {
                updateItem(item.id, 'jr_cost', null);
              } else {
                updateItem(
                  item.id,
                  'jr_cost',
                  +v.target.value < 0 ? 0 : +v.target.value,
                );
              }
            }}
          />
        ),
        plus_cost: (
          <Input
            data-testid="plus-cost-input"
            error={errors[`${item.id}-plus_cost`]?.isInvalid}
            startAdornment="$"
            placeholder="0"
            type="number"
            value={item.plus_cost ?? ''}
            onKeyDown={stopKeyDownPropagation}
            onChange={(v) => {
              if (v.target.value === '') {
                updateItem(item.id, 'plus_cost', null);
              } else {
                updateItem(
                  item.id,
                  'plus_cost',
                  +v.target.value < 0 ? 0 : +v.target.value,
                );
              }
            }}
          />
        ),
        lead_time_in_weeks: (
          <Input
            data-testid="lead-time-in-weeks-input"
            error={errors[`${item.id}-lead_time_in_weeks`]?.isInvalid}
            endAdornment={<p className="text-base-disabled">weeks</p>}
            type="number"
            placeholder="0"
            value={item.lead_time_in_weeks ?? ''}
            onKeyDown={stopKeyDownPropagation}
            onChange={(v) => {
              if (v.target.value === '') {
                updateItem(item.id, 'lead_time_in_weeks', null);
              } else {
                if (!(+v.target.value % 1 === 0)) {
                  return;
                }
                updateItem(
                  item.id,
                  'lead_time_in_weeks',
                  +v.target.value < 0 ? 0 : +v.target.value,
                );
              }
            }}
          />
        ),
        trash: (
          <IconButton
            data-testid="trash-button"
            color="secondary"
            label="Remove Item"
            icon={<Icon name="delete/delete-dustbin-02" size="20px" />}
            disabled={items.length <= 1}
            onClick={() => removeItem(item.id)}
          />
        ),
      })),
    [items, errors],
  );

  return (
    <DataGrid
      columns={columns}
      rows={transformedRows}
      hideFooter
      hideFilterButton
      hideGroupButton
      hideRowHover
      autosizeOnMount
      rowSelection={false}
      pageSizeOptions={[500]}
      initialState={{
        pagination: { paginationModel: { pageSize: 500 } },
      }}
    />
  );
}

export default AddItemsGrid;
